import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { CommunityProfileField, ProfileFieldErrors, ProfileFieldValue } from '~/interfaces/community';

import { PillButton } from '../../_components/ui/PillButton';

import { ProfileFieldInput } from '../../_components/ProfileFieldInput';
import { submitLabel } from '../../_utils/submitLabel';
import { OnboardHeader } from './OnboardHeader';

type OnboardQuestionsPageProps = {
  fields: CommunityProfileField[];
  answers: Record<string, ProfileFieldValue>;
  errors: ProfileFieldErrors;
  firstName: string;
  currentStep: number;
  totalSteps: number;
  disabled?: boolean;
  isLastPage: boolean;
  canGoBack: boolean;
  title?: string;
  subtitle?: string;
  onChange: (fieldId: string, value: ProfileFieldValue) => void;
  onBack: () => void;
};

export const OnboardQuestionsPage: FC<OnboardQuestionsPageProps> = ({
  fields,
  answers,
  errors,
  firstName,
  currentStep,
  totalSteps,
  disabled,
  isLastPage,
  canGoBack,
  title,
  subtitle,
  onChange,
  onBack,
}) => {
  const { t } = useTranslation();
  const welcomeName = firstName.trim();

  return (
    <>
      <OnboardHeader
        title={
          title ??
          (welcomeName
            ? t('community.onboard.welcome_title', { name: welcomeName })
            : t('community.onboard.questions_title'))
        }
        subtitle={subtitle ?? t('community.onboard.questions_subtitle')}
        currentStep={currentStep}
        totalSteps={totalSteps}
      />
      <div className="flex flex-col gap-4">
        {fields.map((field) => (
          <ProfileFieldInput
            key={field.id}
            field={field}
            value={answers[field.id] ?? null}
            error={errors[field.id]?.[0]}
            disabled={disabled}
            onChange={(value) => onChange(field.id, value)}
          />
        ))}
      </div>
      <div className={canGoBack ? 'flex gap-3' : 'flex'}>
        {canGoBack ? (
          <PillButton
            variant="secondary"
            type="button"
            size="lg"
            className="flex-1"
            disabled={disabled}
            onClick={onBack}
          >
            {t('community.onboard.back')}
          </PillButton>
        ) : null}
        <PillButton
          variant="primary"
          type="submit"
          size="lg"
          fullWidth={!canGoBack}
          className={canGoBack ? 'flex-1' : undefined}
          disabled={disabled}
        >
          {submitLabel(t, disabled, isLastPage)}
        </PillButton>
      </div>
    </>
  );
};
